/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { useQueryParams } from '@upright/ui/common/generic/hooks/url-handling/query-params';
import { saveNetImpactLenseSelectionOptions } from '@upright/ui/common/generic/utilities/storage';
import { EmptySelectionState } from '@upright/ui/common/upright/core/impact-lens-profile/selection-state/empty-selection-state';
import { LeftAndRightImpactSelectionState } from '@upright/ui/common/upright/core/impact-lens-profile/selection-state/separate-left-right-selection-state';
import { MaterialitySelectionState } from '@upright/ui/common/upright/core/impact-lenses/materiality/selection-state';
import { FinancialEffectsSelectionState } from '@upright/ui/common/upright/core/impact-lenses/financial-effects/selection-state';
import { ClimateRiskSelectionState } from '@upright/ui/common/upright/core/impact-lenses/climate-risk/selection-state';
import {
  defaultNetImpactLensSelectionOptions,
  extractNetImpactLensOptions,
  NetImpactLensSelectionState,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import { PaiLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/pais/selection-state/single-impact-selection-state';
import { SdgLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/sdg/selection-state';
import { isEqual, omitBy } from 'lodash';
import { useEffect, useRef } from 'react';
import { ImpactLensProfileProps, LensData, LensOptions } from '../impact-lens-profile/types';
import { allImpactLenses, ImpactLens } from './types';

export type LensSelectionStates = {
  [ImpactLensType.UPRIGHT_NET_IMPACT]: NetImpactLensSelectionState;
  [ImpactLensType.INSIGHTS]: LeftAndRightImpactSelectionState;
  [ImpactLensType.EU_PAIS]: PaiLensSelectionState;
  [ImpactLensType.EU_TAXONOMY]: EmptySelectionState;
  [ImpactLensType.SDGS]: SdgLensSelectionState;
  [ImpactLensType.TARGETS]: LeftAndRightImpactSelectionState;
  [ImpactLensType.CSRD]: MaterialitySelectionState;
  [ImpactLensType.FINANCIAL_EFFECTS]: FinancialEffectsSelectionState;
  [ImpactLensType.CLIMATE_RISK]: ClimateRiskSelectionState;
};

export type LensSelectionStateHandler = [
  Partial<SerializedLensSelectionStateParams>,
  (paramsToSet: Partial<SerializedLensSelectionStateParams>) => void,
];

export type SerializedLensSelectionStateParams = {
  [ImpactLensType.UPRIGHT_NET_IMPACT]: string;
  [ImpactLensType.INSIGHTS]: string;
  [ImpactLensType.EU_PAIS]: string;
  [ImpactLensType.EU_TAXONOMY]: string;
  [ImpactLensType.SDGS]: string;
  [ImpactLensType.TARGETS]: string;
  [ImpactLensType.CSRD]: string;
  [ImpactLensType.FINANCIAL_EFFECTS]: string;
  [ImpactLensType.CLIMATE_RISK]: string;
};

/**
 * Hook that allows reads and edits to the whole list of lens selection states stored in url
 */
function useUrlSelectionState(selectionStateHandler?: LensSelectionStateHandler) {
  const [queryParamsMaybe, setQueryParams] =
    // OA: It's ok to call the hook conditionally, because the prop won't change during the lifetime of the component
    // eslint-disable-next-line react-hooks/rules-of-hooks
    selectionStateHandler || useQueryParams<SerializedLensSelectionStateParams>(allImpactLenses);
  const setState = (lens: ImpactLensType, newState: string | undefined) => {
    const newStateMerged = { ...queryParamsMaybe, [lens]: newState };

    setQueryParams(newStateMerged);
  };
  return [queryParamsMaybe, setState] as const;
}

/**
 * Hook that only gives access to read and edit state corresponding to the given lensType.
 */
export function useSelectionStateInLens<
  A extends ImpactLensType,
  B extends LensData<A>,
  C extends LensOptions<A>,
  D extends ImpactLens<A, B, C>,
>(props: ImpactLensProfileProps<A, B, C, D>) {
  const lens = props.lens;
  const lensType = lens.type;
  const selectionStateHandler = props.options?.selectionStateHandler;
  const { defaultSelectionState, serializeSelectionState, unserializeSelectionState } = lens;

  const [fullState, setLensState] = useUrlSelectionState(selectionStateHandler);
  const state = fullState[lensType];

  const unserialized = unserializeSelectionState(state);

  const mergedState = defaultSelectionState
    ? { ...defaultSelectionState, ...omitBy(unserialized, val => (val as any) === undefined) }
    : unserialized;

  const hasSetImpactLensUrlQueryParamsOnLoad = useRef(false);

  useEffect(() => {
    if (lensType === ImpactLensType.UPRIGHT_NET_IMPACT && !hasSetImpactLensUrlQueryParamsOnLoad.current) {
      hasSetImpactLensUrlQueryParamsOnLoad.current = true;
      if (
        !Object.keys(unserialized as NetImpactLensSelectionState).length &&
        !isEqual(defaultSelectionState, defaultNetImpactLensSelectionOptions)
      ) {
        // Set query parameters to stored user options upon landing on a page with no query parameters
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const serialized = serializeSelectionState(defaultSelectionState!);
        setLensState(lensType, serialized);
      }
    }
  }, [lensType, unserialized, defaultSelectionState, serializeSelectionState, setLensState]);

  const setState = (newState: LensSelectionStates[A]) => {
    if (lensType === ImpactLensType.UPRIGHT_NET_IMPACT) {
      const netImpactLensOptions = extractNetImpactLensOptions(newState as NetImpactLensSelectionState);
      saveNetImpactLenseSelectionOptions(netImpactLensOptions);
    }
    const serialized = serializeSelectionState(newState);
    setLensState(lensType, serialized);
  };

  return [mergedState, setState] as const;
}
